import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  MarkEmailRead as ReadIcon,
  MarkEmailUnread as UnreadIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  PhoneMissed as MissedIcon,
  PhoneCallback as OutboundIcon,
  Voicemail as VoicemailIcon,
  PhoneInTalk as AnsweredIcon,
  CallReceived as InboundIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';

const formatDuration = (seconds) => {
  if (!seconds) return '--';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const statusConfig = {
  missed: { label: 'Missed', color: 'error', icon: <MissedIcon fontSize="small" /> },
  answered: { label: 'Answered', color: 'success', icon: <AnsweredIcon fontSize="small" /> },
  voicemail: { label: 'Voicemail', color: 'warning', icon: <VoicemailIcon fontSize="small" /> },
  no_answer: { label: 'No Answer', color: 'default', icon: <MissedIcon fontSize="small" /> },
};

const Voicemails = () => {
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [audioUrls, setAudioUrls] = useState({});
  const [loadingAudio, setLoadingAudio] = useState({});

  // Return call dialog
  const [returnCallDialog, setReturnCallDialog] = useState(null);
  const [adminPhone, setAdminPhone] = useState('');
  const [callingBack, setCallingBack] = useState(false);

  // Delete confirmation (voicemails only)
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadCallLogs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getCallLogs({ page, limit: rowsPerPage, filter });
      setCallLogs(res.data.callLogs);
      setTotal(res.data.total);
    } catch (err) {
      setError('Failed to load call logs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    loadCallLogs();
  }, [loadCallLogs]);

  const handleExpand = async (entry) => {
    if (expandedId === entry.id) {
      setExpandedId(null);
      return;
    }

    setExpandedId(entry.id);

    // Auto-mark as read
    if (!entry.is_read) {
      try {
        await adminAPI.markCallLogRead(entry.id, true);
        setCallLogs(prev => prev.map(cl => cl.id === entry.id ? { ...cl, is_read: true } : cl));
        window.dispatchEvent(new Event('voicemail-updated'));
      } catch (err) {
        // Non-critical
      }
    }

    // Load audio URL if this entry has a voicemail
    if (entry.voicemail_id && !audioUrls[entry.voicemail_id]) {
      setLoadingAudio(prev => ({ ...prev, [entry.voicemail_id]: true }));
      try {
        const res = await adminAPI.getVoicemailAudio(entry.voicemail_id);
        setAudioUrls(prev => ({ ...prev, [entry.voicemail_id]: res.data.url }));
      } catch (err) {
        console.error('Failed to load audio:', err);
      } finally {
        setLoadingAudio(prev => ({ ...prev, [entry.voicemail_id]: false }));
      }
    }
  };

  const handleToggleRead = async (id, currentlyRead) => {
    try {
      await adminAPI.markCallLogRead(id, !currentlyRead);
      setCallLogs(prev => prev.map(cl => cl.id === id ? { ...cl, is_read: !currentlyRead } : cl));
      window.dispatchEvent(new Event('voicemail-updated'));
    } catch (err) {
      setError('Failed to update read status');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      setDeleting(true);
      await adminAPI.deleteVoicemail(deleteConfirm);
      setDeleteConfirm(null);
      setSuccess('Voicemail deleted');
      loadCallLogs();
      window.dispatchEvent(new Event('voicemail-updated'));
    } catch (err) {
      setError('Failed to delete voicemail');
    } finally {
      setDeleting(false);
    }
  };

  const handleReturnCall = async () => {
    if (!returnCallDialog || !adminPhone.trim()) return;
    try {
      setCallingBack(true);
      // Use voicemail_id if available, otherwise use caller number directly
      const entry = callLogs.find(cl => cl.id === returnCallDialog);
      if (entry?.voicemail_id) {
        await adminAPI.initiateReturnCall(entry.voicemail_id, adminPhone.trim());
      } else {
        setError('Return call requires a voicemail entry. Use the phone number to call directly.');
        setReturnCallDialog(null);
        setAdminPhone('');
        return;
      }
      setReturnCallDialog(null);
      setAdminPhone('');
      setSuccess('Return call initiated. Your phone will ring shortly.');
    } catch (err) {
      setError('Failed to initiate return call: ' + (err.response?.data?.error || err.message));
    } finally {
      setCallingBack(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Call Log
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        All calls to and from the support line.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Filter */}
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(e, val) => { if (val) { setFilter(val); setPage(0); } }}
          size="small"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="missed">Missed</ToggleButton>
          <ToggleButton value="answered">Answered</ToggleButton>
          <ToggleButton value="voicemail">Voicemails</ToggleButton>
          <ToggleButton value="outbound">Outgoing</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>Caller</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : callLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No calls found.
                  </TableCell>
                </TableRow>
              ) : (
                callLogs.map((entry) => {
                  const config = statusConfig[entry.status] || statusConfig.missed;
                  const hasVoicemail = !!entry.voicemail_id;
                  const hasExpandableContent = hasVoicemail || entry.notes;
                  const duration = entry.vm_duration_seconds || entry.duration_seconds;

                  return (
                    <React.Fragment key={entry.id}>
                      <TableRow
                        hover
                        onClick={() => hasExpandableContent && handleExpand(entry)}
                        sx={{
                          cursor: hasExpandableContent ? 'pointer' : 'default',
                          fontWeight: entry.is_read ? 'normal' : 'bold',
                          bgcolor: !entry.is_read ? 'action.hover' : 'inherit',
                        }}
                      >
                        <TableCell>
                          {hasExpandableContent && (
                            <IconButton size="small">
                              {expandedId === entry.id ? <CollapseIcon /> : <ExpandIcon />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={entry.is_read ? 400 : 600}>
                            {entry.caller_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(entry.created_at)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={entry.direction === 'outbound' ? <OutboundIcon /> : <InboundIcon />}
                            label={entry.direction === 'outbound' ? 'Outgoing' : 'Incoming'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatDuration(duration)}</TableCell>
                        <TableCell>
                          <Chip
                            icon={config.icon}
                            label={config.label}
                            size="small"
                            color={config.color}
                            variant={entry.status === 'missed' ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title={entry.is_read ? 'Mark unread' : 'Mark read'}>
                            <IconButton size="small" onClick={() => handleToggleRead(entry.id, entry.is_read)}>
                              {entry.is_read ? <UnreadIcon fontSize="small" /> : <ReadIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          {hasVoicemail && (
                            <>
                              <Tooltip title="Return call">
                                <IconButton size="small" onClick={() => { setReturnCallDialog(entry.id); setAdminPhone(''); }}>
                                  <PhoneIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete voicemail">
                                <IconButton size="small" color="error" onClick={() => setDeleteConfirm(entry.voicemail_id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                      {hasExpandableContent && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 0, borderBottom: expandedId === entry.id ? undefined : 'none' }}>
                            <Collapse in={expandedId === entry.id} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 1 }}>
                                {hasVoicemail && (
                                  <>
                                    {/* Audio Player */}
                                    <Typography variant="subtitle2" gutterBottom>Recording</Typography>
                                    {loadingAudio[entry.voicemail_id] ? (
                                      <CircularProgress size={20} />
                                    ) : audioUrls[entry.voicemail_id] ? (
                                      <audio controls src={audioUrls[entry.voicemail_id]} style={{ width: '100%', maxWidth: 500 }} />
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">No audio available</Typography>
                                    )}

                                    {/* Transcription */}
                                    <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Transcription</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {entry.transcription || 'No transcription available.'}
                                    </Typography>
                                  </>
                                )}
                                {entry.notes && (
                                  <>
                                    <Typography variant="subtitle2" sx={{ mt: hasVoicemail ? 2 : 0 }} gutterBottom>Notes</Typography>
                                    <Typography variant="body2">{entry.notes}</Typography>
                                  </>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>

      {/* Return Call Dialog */}
      <Dialog open={!!returnCallDialog} onClose={() => setReturnCallDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Return Call</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your phone number. We'll call you first, then connect you to the caller.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Your Phone Number"
            placeholder="+1XXXXXXXXXX"
            value={adminPhone}
            onChange={(e) => setAdminPhone(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnCallDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleReturnCall}
            disabled={callingBack || !adminPhone.trim()}
            startIcon={callingBack ? <CircularProgress size={16} color="inherit" /> : <PhoneIcon />}
          >
            {callingBack ? 'Calling...' : 'Call'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Voicemail</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this voicemail? This will remove the recording from storage.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Voicemails;
